import React, { useEffect, useState } from 'react';
import { MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useToast } from 'use-toast-mui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { grantGroupPoints } from '../../../../api/member-point';
import { getBranchManager } from '../../../../api/staff';
import { POINT_TYPE } from '../../../../constants/point';
import { errorMessages } from '../../../../utils/methods';
import PointExpireField from '../../../point/PointExpireField';
import CommonModalLayout from '../../../../form-components/CommonModalLayout';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonSegmentedControl from '../../../../form-components/CommonSegmentedControl';
import { FormInputDropdown } from '../../../../form-components/FormInputDropdown';
import { FormInputPrice } from '../../../../form-components/FormInputPrice';
import { FormInputText } from '../../../../form-components/FormInputText';
import {
  FieldGroup,
  FieldLabel,
  FieldsBox,
  MemoWrapper,
  PointSuffix,
  PreviewBox,
  PreviewHighlight,
  SegmentWrapper,
} from '../../../userDetails/UserPointHistory/UserPointHistoryStyled';

const TYPE_OPTIONS = [
  { value: POINT_TYPE.EARN, label: '적립' },
  { value: POINT_TYPE.DEDUCT, label: '차감' },
];

// 회원 상세 포인트 모달과 같은 길이 제한을 쓴다.
const MEMO_MAX_LENGTH = 10;

const DEFAULT_VALUES = { staffUserId: '', point: '', memo: '' };

/**
 * 회원 그룹 포인트 일괄 적립·차감 모달. 그룹 관리 모달과 포인트 관리 화면이 함께 쓴다.
 *
 * <p>서버는 <b>전원 성공해야 반영</b>한다. 한 명이라도 잔액이 모자라면 아무에게도 들어가지 않고
 * 누가 부족한지 메시지로 온다. 그래서 화면에서는 개별 성공/실패를 세지 않는다.
 *
 * @param memberIds 비우면 그룹 전원이 대상이다. 그룹 모달은 체크한 회원만 보낸다.
 * @param targetCount 미리보기에 쓸 대상 인원. memberIds 가 있으면 그 길이와 같다.
 */
const GroupPointGrantModal = ({
  open,
  onClose,
  groupId,
  groupName,
  branchId,
  token,
  memberIds,
  targetCount,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [type, setType] = useState(POINT_TYPE.EARN);
  // 지급분 유효기간 { expireDate, noExpire }. 둘 다 비면 지점 규칙. 차감에는 보내지 않는다.
  const [expire, setExpire] = useState({ expireDate: null, noExpire: false });

  const { control, handleSubmit, reset, watch } = useForm({ defaultValues: DEFAULT_VALUES });
  const watchedPoint = watch('point');

  // 모달을 닫았다 다시 열면 이전 입력이 남지 않게 한다.
  useEffect(() => {
    if (!open) {
      setType(POINT_TYPE.EARN);
      reset(DEFAULT_VALUES);
    }
  }, [open, reset]);

  const { data: staffOptions = [] } = useQuery({
    queryKey: ['branchManager', branchId],
    queryFn: async () => {
      const data = await getBranchManager(branchId, token);
      return (data || []).map(item => ({ value: item.managerId, label: item.managerName }));
    },
    enabled: open && !!branchId && !!token,
  });

  const grantMutation = useMutation({
    mutationFn: payload => grantGroupPoints(groupId, payload, token),
    onSuccess: result => {
      const label = type === POINT_TYPE.EARN ? '적립' : '차감';
      toast.success(
        `${result?.memberCount ?? 0}${t('명에게')} ${(result?.totalPoint ?? 0).toLocaleString()}P ${t(label)}${t('되었습니다')}`,
      );
      // 지점 포인트 내역(포인트 관리 화면)과 회원별 포인트 잔액을 모두 다시 읽는다.
      queryClient.invalidateQueries({ queryKey: ['branchPointHistories', branchId] });
      queryClient.invalidateQueries({ queryKey: ['memberPoints'] });
      queryClient.invalidateQueries({ queryKey: ['bulkPointBatches', branchId] });
      onClose();
    },
    onError: error => toast.error(errorMessages(error)),
  });

  const handleSave = handleSubmit(values => {
    if (!values.staffUserId) {
      toast.error(t('담당자를 선택해 주세요'));
      return;
    }
    const pointNum = Number(values.point);
    if (values.point === '' || Number.isNaN(pointNum) || pointNum <= 0) {
      toast.error(t(type === POINT_TYPE.EARN ? '적립할 포인트를 입력해 주세요' : '차감할 포인트를 입력해 주세요'));
      return;
    }
    if (!targetCount) {
      toast.error(t('대상 회원이 없습니다'));
      return;
    }
    grantMutation.mutate({
      branchId,
      type,
      point: pointNum,
      staffUserId: values.staffUserId,
      memo: values.memo,
      memberIds: memberIds?.length ? memberIds : undefined,
      expireDate: type === POINT_TYPE.EARN ? expire.expireDate : undefined,
      noExpire: type === POINT_TYPE.EARN && expire.noExpire ? true : undefined,
    });
  });

  const totalPoint = Number(watchedPoint || 0) * (targetCount || 0);

  return (
    <CommonModalLayout
      open={open}
      onClose={onClose}
      title={groupName ? `${groupName} · ${t('포인트')}` : t('포인트')}
      width={480}
      footer={
        <>
          <CommonModalButton variant="cancel" onClick={onClose}>
            {t('취소')}
          </CommonModalButton>
          <CommonModalButton
            variant="submit"
            onClick={handleSave}
            disabled={grantMutation.isPending}>
            {t('저장')}
          </CommonModalButton>
        </>
      }>
      <SegmentWrapper>
        <CommonSegmentedControl
          options={TYPE_OPTIONS.map(opt => ({ ...opt, label: t(opt.label) }))}
          value={type}
          onChange={setType}
        />
      </SegmentWrapper>

      <FieldsBox>
        <FieldGroup>
          <FieldLabel>{t('담당자')}</FieldLabel>
          <FormInputDropdown
            control={control}
            name="staffUserId"
            options={staffOptions}
            disabledItem={
              <MenuItem disabled value="">
                {t('담당자를 선택해 주세요')}
              </MenuItem>
            }
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>
            {type === POINT_TYPE.EARN ? t('1인당 적립 포인트') : t('1인당 차감 포인트')}
          </FieldLabel>
          <FormInputPrice
            control={control}
            name="point"
            size="small"
            placeholder={t('숫자 입력')}
            InputProps={{ endAdornment: <PointSuffix>P</PointSuffix> }}
          />
        </FieldGroup>
      </FieldsBox>

      {type === POINT_TYPE.EARN && (
        <PointExpireField branchId={branchId} token={token} onChange={setExpire} resetKey={open} />
      )}

      {/* 같은 금액이 인원수만큼 나가므로, 누르기 전에 총액을 보여준다. */}
      <PreviewBox>
        {t('대상')} {targetCount || 0}
        {t('명')} · {t('총')}
        <PreviewHighlight>&nbsp;{totalPoint.toLocaleString()}P</PreviewHighlight>
      </PreviewBox>

      <MemoWrapper>
        <FieldLabel>{t('메모')}</FieldLabel>
        <FormInputText
          control={control}
          name="memo"
          placeholder={t('10자 이내로 작성해 주세요.')}
          size="small"
          inputProps={{ maxLength: MEMO_MAX_LENGTH }}
        />
      </MemoWrapper>
    </CommonModalLayout>
  );
};

export default GroupPointGrantModal;
